/* eslint-disable react/require-default-props */
import React from 'react';
import Loader from 'components/atoms/loader';
import AiManagementStatusBadge from 'components/atoms/ai_management_status_badge';
import AiManagementCollapsibleCard from 'components/molecules/ai_management_collapsible_card';
import AiManagementDataCard from 'components/molecules/ai_management_data_card';
import { usePossibleCompetitorSanctions } from 'hooks/usePossibleCompetitorSanctions';
import { PossibleCompetitorSanction } from 'services/actions/ai_management_possible_competitors';
import { EMPTY_VALUE, dateBr } from 'services/ai_management_format';
import { Content } from './styles';

interface AiManagementCompetitorPenaltiesProps {
  biddingId: number | null;
  cnpj: string;
  className?: string;
}

const periodOf = (sanction: PossibleCompetitorSanction) => {
  const start = dateBr(sanction.inicio);
  const end = dateBr(sanction.fim);
  if (start === EMPTY_VALUE && end === EMPTY_VALUE) return null;
  return `${start} a ${end}`;
};

const detailsOf = (sanction: PossibleCompetitorSanction) =>
  [sanction.orgao, sanction.portal, periodOf(sanction)].filter(Boolean).join(' • ');

const AiManagementCompetitorPenalties: React.FC<AiManagementCompetitorPenaltiesProps> = ({
  biddingId,
  cnpj,
  className = '',
}) => {
  const { sanctions, loading, error } = usePossibleCompetitorSanctions(biddingId, cnpj);

  const renderBody = () => {
    if (loading) {
      return (
        <div className="py-3">
          <Loader />
        </div>
      );
    }
    if (error) {
      return (
        <div className="alert alert-warning mb-0" role="alert">
          Não foi possível carregar as penalidades desta empresa.
        </div>
      );
    }
    if (!sanctions.length) {
      return <div className="text-muted">Nenhuma penalidade registrada para esta empresa.</div>;
    }
    return (
      <AiManagementDataCard compact>
        <ul className="list-group list-group-flush">
          {sanctions.map((sanction) => {
            const label = sanction.categoria ?? 'Penalidade';
            const details = detailsOf(sanction);
            return (
              <li
                key={String(sanction.id ?? `${label}-${sanction.inicio}`)}
                className="list-group-item d-flex align-items-start justify-content-between px-3 py-3"
              >
                <div className="mr-3">
                  <div className="penalty-title">
                    {sanction.url ? (
                      <a href={sanction.url} target="_blank" rel="noreferrer">
                        {label}
                      </a>
                    ) : (
                      label
                    )}
                  </div>
                  {details ? <small className="text-muted">{details}</small> : null}
                </div>
                <AiManagementStatusBadge tone={sanction.vigente ? 'danger' : 'neutral'}>
                  {sanction.vigente ? 'Vigente' : 'Encerrada'}
                </AiManagementStatusBadge>
              </li>
            );
          })}
        </ul>
      </AiManagementDataCard>
    );
  };

  return (
    <AiManagementCollapsibleCard title="Penalidades" className={className}>
      <Content>{renderBody()}</Content>
    </AiManagementCollapsibleCard>
  );
};

export default AiManagementCompetitorPenalties;
